import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { NavIcons } from '../navItems';

const DepartmentsIcon = NavIcons.departments;
const EXPAND_KEY = 'rtg.deptNav.expanded';

/**
 * Two letters standing in for a department on the icon-only rail, where the
 * row would otherwise be an unlabelled dot: first letters of the first two
 * words ("Human Resources" -> HR), or the first two letters of a one-word
 * name ("Finance" -> FI). Words that don't start with a letter or digit
 * ("&", "-") are skipped.
 */
export function deptInitials(name) {
  const words = String(name || '').split(/\s+/).filter((w) => /^[\p{L}\p{N}]/u.test(w));
  if (words.length === 0) return '?';
  const letters = words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0];
  return letters.toUpperCase();
}

/**
 * The departments this user manages, from /api/dept/managed. Shared by this
 * sidebar group and the TopBar's phone menu (where the sidebar is hidden), so
 * both list the same departments. `null` until the read settles; a failed read
 * resolves to [] (the server 403s non-managers). `enabled=false` skips the call.
 */
export function useManagedDepartments(enabled = true) {
  const [departments, setDepartments] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    axios
      .get(`${API_BASE_URL}/api/dept/managed`, { withCredentials: true })
      .then((r) => { if (active) setDepartments(r.data.departments || []); })
      .catch(() => { if (active) setDepartments([]); });
    return () => { active = false; };
  }, [enabled]);
  return departments;
}

/**
 * Expandable "My Departments" sidebar group. Renders only when the user
 * manages at least one department (/api/dept/managed returns rows).
 * `labelsHidden`: the rail is icon-only (collapsed, or 601-900px), so each row
 * gets a native title; with visible labels a title would only repeat them.
 * `iconOnly`: no visible label (the rail, or collapsed), so each department
 * shows its initials instead of a bare dot. The initials are aria-hidden; the
 * clipped label stays the accessible name.
 */
export default function MyDepartmentsNav({ labelsHidden = false, iconOnly = labelsHidden }) {
  const navigate = useNavigate();
  const location = useLocation();
  const departments = useManagedDepartments() || [];
  const onDeptRoute = location.pathname.startsWith('/dept/');

  const [expanded, setExpanded] = useState(() => {
    try {
      const stored = window.localStorage.getItem(EXPAND_KEY);
      if (stored !== null) return stored === '1';
    } catch { /* ignore */ }
    return true;
  });

  useEffect(() => { if (onDeptRoute) setExpanded(true); }, [onDeptRoute]);

  const toggle = () => {
    setExpanded((prev) => {
      const next = !prev;
      try { window.localStorage.setItem(EXPAND_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  if (departments.length === 0) return null;

  return (
    <>
      <button
        type="button"
        className="sidebar-nav-item sidebar-nav-item--group"
        aria-expanded={expanded}
        title={labelsHidden ? 'My Departments' : undefined}
        onClick={toggle}
      >
        <DepartmentsIcon />
        <span className="sidebar-nav-label">My Departments</span>
        <svg
          className={`sidebar-nav-caret ${expanded ? 'is-open' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {expanded &&
        departments.map((d) => {
          const active = location.pathname === `/dept/${d.id}`;
          return (
            <a
              key={d.id}
              href={`/dept/${d.id}`}
              title={labelsHidden ? d.name : undefined}
              aria-current={active ? 'page' : undefined}
              className={`sidebar-nav-item sidebar-nav-item--sub ${active ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); navigate(`/dept/${d.id}`); }}
            >
              {iconOnly ? (
                <span className="sidebar-nav-initials" aria-hidden="true">{deptInitials(d.name)}</span>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
              <span className="sidebar-nav-label">{d.name}</span>
            </a>
          );
        })}
    </>
  );
}
